import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MapPin, Plus, Trash, MagnifyingGlass } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PincodeMatrixPage() {
  const [pincodes, setPincodes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ pincode: '', city: '', state: '', is_serviceable: true, delivery_days: 3, cod_available: true });

  useEffect(() => { fetchPincodes(); }, [page, search]);

  const fetchPincodes = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/pincodes?page=${page}&limit=50&search=${search}`, { withCredentials: true });
      setPincodes(data.pincodes || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load pincodes'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!form.pincode) { toast.error('Pincode is required'); return; }
    try {
      await axios.post(`${API}/api/seller/pincodes`, form, { withCredentials: true });
      toast.success('Pincode added');
      setDialogOpen(false);
      setForm({ pincode: '', city: '', state: '', is_serviceable: true, delivery_days: 3, cod_available: true });
      fetchPincodes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
  };

  const handleDelete = async (pid) => {
    try {
      await axios.delete(`${API}/api/seller/pincodes/${pid}`, { withCredentials: true });
      toast.success('Pincode removed');
      fetchPincodes();
    } catch { toast.error('Failed to delete'); }
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl" data-testid="pincode-matrix-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Pincode Serviceability</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Manage serviceable pincodes ({total} total)</p>
          </div>
          <Button data-testid="add-pincode-btn" onClick={() => setDialogOpen(true)} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <Plus size={16} className="mr-2" /> Add Pincode
          </Button>
        </div>

        <div className="relative mb-4 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9c9db7]" />
          <Input data-testid="pincode-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search pincode or city..." className="pl-9" />
        </div>

        {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : pincodes.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <MapPin size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No pincodes configured yet.</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-[#e5e5ea] overflow-hidden">
              <table className="w-full text-sm" data-testid="pincode-table">
                <thead className="bg-[#f9f9fb] border-b border-[#e5e5ea]">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Pincode</th>
                    <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">City</th>
                    <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">State</th>
                    <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Delivery</th>
                    <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">COD</th>
                    <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Status</th>
                    <th className="text-right px-4 py-3 font-medium text-[#6b6b8a]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pincodes.map(p => (
                    <tr key={p.pincode_id} className="border-b border-[#f0f0f0] hover:bg-[#fafafe]">
                      <td className="px-4 py-3 font-mono font-medium text-[#0A0A0A]">{p.pincode}</td>
                      <td className="px-4 py-3 text-[#6b6b8a]">{p.city}</td>
                      <td className="px-4 py-3 text-[#6b6b8a]">{p.state}</td>
                      <td className="px-4 py-3">{p.delivery_days}d</td>
                      <td className="px-4 py-3">{p.cod_available ? <span className="text-green-600 text-xs font-medium">Yes</span> : <span className="text-red-500 text-xs font-medium">No</span>}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.is_serviceable ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{p.is_serviceable ? 'Active' : 'Inactive'}</span></td>
                      <td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" onClick={() => handleDelete(p.pincode_id)} className="text-red-500"><Trash size={14} /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                <span className="text-sm text-[#6b6b8a] py-1.5">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            )}
          </>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-sm" data-testid="pincode-dialog">
            <DialogHeader><DialogTitle>Add Pincode</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Pincode</Label><Input data-testid="pincode-input" value={form.pincode} onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))} placeholder="110001" /></div>
              <div><Label>City</Label><Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="New Delhi" /></div>
              <div><Label>State</Label><Input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} placeholder="Delhi" /></div>
              <div><Label>Delivery Days</Label><Input type="number" value={form.delivery_days} onChange={e => setForm(f => ({ ...f, delivery_days: parseInt(e.target.value) || 3 }))} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.cod_available} onChange={e => setForm(f => ({ ...f, cod_available: e.target.checked }))} className="rounded" /> COD Available</label>
              <Button data-testid="save-pincode-btn" onClick={handleAdd} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]">Add Pincode</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
